import { useState } from 'react';
import { NavMarker } from 'react-rhumb';
import { fromJS } from 'immutable';
import { TABLE } from 'reporting-data/constants/chartTypes';
import { AGGREGATION } from 'reporting-data/constants/configTypes';
import { ANALYTICS_ALL_PAGES } from 'reporting-data/constants/dataTypes';
import { IN } from 'reporting-data/constants/operators';
import { ASC, DESC } from 'reporting-data/constants/sortOrder';
import { fromMetricKey } from 'reporting-data/v2/dataset/datasetMetrics';
import PlatformReportResolver from 'reporting-ui-components/components/PlatformReport/PlatformReportResolver';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Link } from 'foundations-components/transitional/components/Link';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { useContentObjectType } from 'content-management-data-lib';
import { getDetailsUrl } from 'page-lib/utils/urls';
import { ALL_METRICS, DEFAULT_VARIATION_METRICS_TO_DISPLAY } from '../../constants/reporting';
import useContentId from '../../hooks/useContentId';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VariantName = ({
  mabVariant,
  contentId,
  contentObjectType
}) => {
  if (mabVariant.id === contentId) {
    return /*#__PURE__*/_jsx("span", {
      children: mabVariant.name
    });
  }
  const detailsUrl = getDetailsUrl({
    contentObjectType,
    id: mabVariant.id
  });
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    href: detailsUrl,
    children: mabVariant.name
  });
};
const VariantStatus = ({
  mabVariant
}) => {
  let state = mabVariant.currentState;
  if (mabVariant.isDeleted) {
    state = 'deleted';
  } else if (mabVariant.archived) {
    state = 'ARCHIVED';
  }
  const statusDot = mabVariant.isPublished ? /*#__PURE__*/_jsx(StatusDot, {
    use: "success"
  }) : mabVariant.archived && !mabVariant.isDeleted ? /*#__PURE__*/_jsx(StatusDot, {
    hollow: true,
    use: "default"
  }) : null;
  return /*#__PURE__*/_jsxs(Small, {
    use: "help",
    children: [statusDot, /*#__PURE__*/_jsx(FormattedMessage, {
      message: `page-details-ui.testResults.personalizationResults.mabPastExperimentsTable.statusCell.${state}`
    })]
  });
};
const PastVariantTableCell = ({
  mabVariant,
  contentId,
  contentObjectType
}) => {
  return /*#__PURE__*/_jsx("td", {
    children: /*#__PURE__*/_jsxs(TFlex, {
      gap: 4,
      direction: "column",
      children: [/*#__PURE__*/_jsx(VariantName, {
        contentId: contentId,
        contentObjectType: contentObjectType,
        mabVariant: mabVariant
      }), /*#__PURE__*/_jsx(VariantStatus, {
        mabVariant: mabVariant
      })]
    })
  });
};
const getReport = ({
  dateRange,
  metric,
  metricSortDir,
  mabVariantsMap,
  variantIds,
  handleSortMetricColumn,
  contentId,
  contentObjectType
}) => {
  const columns = DEFAULT_VARIATION_METRICS_TO_DISPLAY.reduce((acc, metricToDisplay) => {
    const {
      tooltipKey
    } = ALL_METRICS[metricToDisplay];
    acc[metricToDisplay] = {
      tooltipI18nKey: tooltipKey,
      onSort: handleSortMetricColumn
    };
    return acc;
  }, {
    id: {
      render: ({
        value
      }) => {
        return /*#__PURE__*/_jsx(PastVariantTableCell, {
          contentObjectType: contentObjectType,
          contentId: contentId,
          mabVariant: mabVariantsMap[value]
        });
      }
    }
  });
  const metrics = DEFAULT_VARIATION_METRICS_TO_DISPLAY.map(metricToDisplay => ({
    metricTypes: ALL_METRICS[metricToDisplay].metricTypes,
    property: metricToDisplay
  }));
  return fromJS({
    chartType: TABLE,
    config: {
      configType: AGGREGATION,
      dataType: ANALYTICS_ALL_PAGES,
      dimensions: ['id'],
      filters: {
        custom: [{
          operator: IN,
          property: 'filters',
          values: variantIds
        }],
        dateRange: {
          property: 'sessionDate',
          value: dateRange
        }
      },
      metrics,
      sort: [{
        order: metricSortDir,
        property: metric
      }]
    },
    displayParams: {
      columns,
      hidePagination: true,
      rhumbConfig: {
        renderSuccessMarker: () => /*#__PURE__*/_jsx(NavMarker, {
          name: "MAB_VARIANTS_TABLE_LOADED"
        })
      },
      shouldTruncateHeader: false,
      showTotals: true
    }
  });
};
const MABPastVariantsTable = ({
  dateRange,
  mabVariantsMap,
  variantIds,
  mabMetric
}) => {
  const {
    contentObjectType
  } = useContentObjectType();
  const contentId = useContentId();
  const [metricSortDir, setMetricSortDirection] = useState(DESC);
  const [metric, setMetric] = useState(mabMetric);
  const handleSortMetricColumn = clickedMetric => {
    const {
      property: clickedProperty
    } = fromMetricKey(clickedMetric);
    if (clickedProperty === metric) {
      const newSortDir = metricSortDir === ASC ? DESC : ASC;
      setMetricSortDirection(newSortDir);
    } else {
      setMetric(clickedProperty);
    }
  };
  const report = getReport({
    variantIds,
    dateRange,
    mabVariantsMap,
    metric,
    metricSortDir,
    handleSortMetricColumn,
    contentId,
    contentObjectType
  });
  if (!report) {
    return null;
  }
  return /*#__PURE__*/_jsx(PlatformReportResolver, {
    report: report,
    useInlineLabels: false
  });
};
export default MABPastVariantsTable;